import React, { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import TextField from './TextField';

const commonDomains = [
  '@gmail.com',
  '@hotmail.com',
  '@outlook.com',
  '@yahoo.com',
  '@icloud.com',
  '@terra.com.br',
  '@uol.com.br',
  '@bol.com.br',
  '@live.com'
];

interface EmailAutoCompleteProps {
  name: string;
  label: string;
  placeholder?: string;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onClipboardChange?: (event: ClipboardEvent<HTMLInputElement>) => void;
}

const EmailAutoComplete: React.FC<EmailAutoCompleteProps> = ({
  name,
  label,
  placeholder,
  className,
  loading,
  disabled,
  size,
  onKeyDown,
  onClipboardChange
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { control, setValue, trigger } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (value: string) => {
    if (!value.includes('@')) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const [, domain] = value.split('@');
    if (!domain) {
      setSuggestions(commonDomains);
      setShowSuggestions(true);
      return;
    }
    const filteredDomains = commonDomains.filter((d) =>
      d.toLowerCase().includes(domain.toLowerCase())
    );
    setSuggestions(filteredDomains);
    setShowSuggestions(true);
  };

  const handleKeyDownInternal = (e: KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) onKeyDown(e);
    if (e.defaultPrevented) return;
    if (e.ctrlKey && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      return;
    }
    if (e.key === ' ') {
      e.preventDefault();
      return;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <TextField
              {...field}
              name={name}
              label={label}
              placeholder={placeholder}
              isEmail
              size={size}
              loading={loading}
              disabled={disabled}
              onClipboardChange={onClipboardChange}
              onKeyDown={handleKeyDownInternal}
              onChange={(e) => {
                field.onChange(e);
                handleInputChange(e.target.value);
              }}
              onFocus={() => {
                if (field.value && field.value.includes('@')) {
                  handleInputChange(field.value);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
                trigger(name);
              }}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      const [localPart] = String(control._formValues?.[name] ?? field.value).split('@');
                      setValue(name, `${localPart}${suggestion}`);
                      setShowSuggestions(false);
                      trigger(name);
                    }}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};

export default EmailAutoComplete;
